export interface FuzzySearchable {
	label: string;
	keywords?: string[];
}

const normalize = (value: string): string =>
	value
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

const levenshtein = (a: string, b: string): number => {
	const previous = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		let diagonal = previous[0];
		previous[0] = i;
		for (let j = 1; j <= b.length; j++) {
			const temp = previous[j];
			previous[j] = Math.min(
				previous[j] + 1,
				previous[j - 1] + 1,
				diagonal + (a[i - 1] === b[j - 1] ? 0 : 1),
			);
			diagonal = temp;
		}
	}
	return previous[b.length];
};

/**
 * Score one query token against one candidate token. Prefix beats
 * substring beats a small typo beats an in-order subsequence.
 */
const scoreToken = (candidate: string, query: string): number => {
	if (candidate === query) return 100;
	if (candidate.startsWith(query)) return 90 - (candidate.length - query.length) * 0.5;
	if (candidate.includes(query)) return 70;

	const allowedTypos = query.length >= 5 ? 2 : query.length >= 3 ? 1 : 0;
	if (allowedTypos > 0) {
		const head = candidate.slice(0, query.length);
		if (levenshtein(head, query) <= allowedTypos) return 60;
	}

	let position = 0;
	for (const ch of query) {
		position = candidate.indexOf(ch, position);
		if (position < 0) return 0;
		position += 1;
	}
	return 30;
};

/**
 * Every query token must match some candidate token. The result score is
 * the sum of the best per-token scores, with a small bonus when the label
 * matched (rather than only a keyword) so exact names outrank aliases.
 */
export const fuzzyScore = (item: FuzzySearchable, query: string): number => {
	const queryTokens = normalize(query).split(' ').filter(Boolean);
	if (queryTokens.length === 0) return 1;

	const labelTokens = normalize(item.label).split(' ');
	const keywordTokens = (item.keywords ?? []).flatMap((k) => normalize(k).split(' '));

	let total = 0;
	for (const queryToken of queryTokens) {
		const fromLabel = Math.max(0, ...labelTokens.map((t) => scoreToken(t, queryToken)));
		const fromKeywords = Math.max(0, ...keywordTokens.map((t) => scoreToken(t, queryToken)));
		const best = Math.max(fromLabel, fromKeywords * 0.9);
		if (best === 0) return 0;
		total += best;
	}
	return total;
};

/**
 * Filter and rank items for a query. A blank query is not a search:
 * the caller's own ordering is returned untouched (for the ingredient
 * select, that's water first, then alphabetical).
 */
export const fuzzyFilter = <T extends FuzzySearchable>(items: T[], query: string): T[] => {
	if (!normalize(query)) {
		return [...items];
	}

	return items
		.map((item) => ({ item, score: fuzzyScore(item, query) }))
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
		.map(({ item }) => item);
};
